import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { integrationsService } from "@/services";
import type {
  IntegrationInstallation,
  ConnectRepoPayload,
} from "@/services/IntegrationsService";
import type {
  SourceControl,
  SourceControlProvider,
} from "@shared/types/ServiceOrderDto";

interface IntegrationsContextType {
  installations: IntegrationInstallation[];
  isLoading: boolean;
  error: string | null;
  fetchInstallations: () => Promise<void>;
  connectRepo: (payload: ConnectRepoPayload) => Promise<SourceControl>;
  disconnectRepo: (serviceOrderId: string) => Promise<void>;
  getInstallUrl: (provider: SourceControlProvider, serviceOrderId: string) => Promise<string>;
  getAvailableRepos: (
    provider: SourceControlProvider,
    installationId: string
  ) => Promise<string[]>;
  hasInstallation: (provider: SourceControlProvider) => boolean;
  getInstallationByProvider: (
    provider: SourceControlProvider
  ) => IntegrationInstallation | undefined;
}

const IntegrationsContext = createContext<IntegrationsContextType | null>(null);

export function IntegrationsProvider({ children }: { children: ReactNode }) {
  const [installations, setInstallations] = useState<IntegrationInstallation[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstallations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await integrationsService.getInstallations();
      setInstallations(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch installations"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const connectRepo = useCallback(
    async (payload: ConnectRepoPayload): Promise<SourceControl> => {
      const result = await integrationsService.connectRepoToProject(payload);
      return result;
    },
    []
  );

  const disconnectRepo = useCallback(async (serviceOrderId: string) => {
    await integrationsService.disconnectRepo(serviceOrderId);
  }, []);

  const getInstallUrl = useCallback(
    async (provider: SourceControlProvider, serviceOrderId: string): Promise<string> => {
      const { url } = await integrationsService.getInstallationUrl(provider, serviceOrderId);
      return url;
    },
    []
  );

  const getAvailableRepos = useCallback(
    async (
      provider: SourceControlProvider,
      installationId: string
    ): Promise<string[]> => {
      const { repos } = await integrationsService.getAvailableRepos(
        provider,
        installationId
      );
      return repos;
    },
    []
  );

  const hasInstallation = useCallback(
    (provider: SourceControlProvider): boolean => {
      return installations.some((i) => i.provider === provider);
    },
    [installations]
  );

  const getInstallationByProvider = useCallback(
    (provider: SourceControlProvider): IntegrationInstallation | undefined => {
      return installations.find((i) => i.provider === provider);
    },
    [installations]
  );

  useEffect(() => {
    fetchInstallations();
  }, [fetchInstallations]);

  return (
    <IntegrationsContext.Provider
      value={{
        installations,
        isLoading,
        error,
        fetchInstallations,
        connectRepo,
        disconnectRepo,
        getInstallUrl,
        getAvailableRepos,
        hasInstallation,
        getInstallationByProvider,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
}

export function useIntegrations() {
  const context = useContext(IntegrationsContext);
  if (!context) {
    throw new Error(
      "useIntegrations must be used within an IntegrationsProvider"
    );
  }
  return context;
}
